import { useEffect, useMemo, useState } from "react";
import ConfirmModal from "./ConfirmModal";
import TenantProfileModal from "./TenantProfileModal";
import { tenantDisplayName } from "../utils/tenantProfile";

const MAILBOX_SIZES = [
  ["SMALL", "Small"],
  ["STANDARD", "Standard"],
  ["LARGE", "Large"],
  ["PARCEL", "Parcel"],
];

const MAILBOX_SIZE_LABELS = Object.fromEntries(MAILBOX_SIZES);

function mailboxValues(mailbox) {
  return {
    tenantId: mailbox.tenant?.id || null,
    size: mailbox.size || "STANDARD",
    hasMail: Boolean(mailbox.hasMail),
    hasPackage: Boolean(mailbox.hasPackage),
  };
}

export default function MailboxEditModal({
  mailbox,
  tenants,
  isSaving = false,
  submitError = "",
  onSubmit,
  onCreateTenant,
  onClose,
}) {
  const [form, setForm] = useState({ tenantId: "", size: "STANDARD", hasMail: false, hasPackage: false });
  const [tenantQuery, setTenantQuery] = useState("");
  const [tenantOptions, setTenantOptions] = useState(tenants);
  const [choosingTenant, setChoosingTenant] = useState(!mailbox.tenant);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [isCreatingTenant, setIsCreatingTenant] = useState(false);
  const [tenantError, setTenantError] = useState("");
  const [tenantFieldErrors, setTenantFieldErrors] = useState({});
  const [pendingUpdate, setPendingUpdate] = useState(null);

  useEffect(() => {
    setForm({
      tenantId: mailbox.tenant?.id ? String(mailbox.tenant.id) : "",
      size: mailbox.size || "STANDARD",
      hasMail: Boolean(mailbox.hasMail),
      hasPackage: Boolean(mailbox.hasPackage),
    });
    setTenantQuery("");
    setChoosingTenant(!mailbox.tenant);
    setPendingUpdate(null);
  }, [mailbox]);

  useEffect(() => setTenantOptions(tenants), [tenants]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !creatingTenant && !pendingUpdate && !isSaving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [creatingTenant, isSaving, onClose, pendingUpdate]);

  const visibleTenants = useMemo(() => {
    const query = tenantQuery.trim().toLowerCase();
    if (!query) return tenantOptions;
    return tenantOptions.filter((tenant) => [tenantDisplayName(tenant), tenant.email, tenant.businessName]
      .some((value) => value?.toLowerCase().includes(query)));
  }, [tenantOptions, tenantQuery]);

  const updatePayload = useMemo(() => ({
    tenantId: form.tenantId ? Number(form.tenantId) : null,
    size: form.size,
    hasMail: form.hasMail,
    hasPackage: form.hasPackage,
  }), [form]);

  const originalValues = useMemo(() => mailboxValues(mailbox), [mailbox]);
  const hasChanges = Object.keys(originalValues)
    .some((field) => originalValues[field] !== updatePayload[field]);

  const tenantLabel = (tenantId) => {
    if (!tenantId) return "No tenant";
    const tenant = tenantOptions.find(({ id }) => id === tenantId)
      || (mailbox.tenant?.id === tenantId ? mailbox.tenant : null);
    return tenant ? tenantDisplayName(tenant) : `Tenant #${tenantId}`;
  };

  const changedItems = () => {
    const items = [];
    if (originalValues.tenantId !== updatePayload.tenantId) {
      items.push(`Tenant: ${tenantLabel(originalValues.tenantId)} → ${tenantLabel(updatePayload.tenantId)}`);
    }
    if (originalValues.size !== updatePayload.size) {
      items.push(`Mailbox size: ${MAILBOX_SIZE_LABELS[originalValues.size]} → ${MAILBOX_SIZE_LABELS[updatePayload.size]}`);
    }
    if (originalValues.hasMail !== updatePayload.hasMail) {
      items.push(`Mail letters: ${originalValues.hasMail ? "Present" : "None"} → ${updatePayload.hasMail ? "Present" : "None"}`);
    }
    if (originalValues.hasPackage !== updatePayload.hasPackage) {
      items.push(`UPS/FedEx package: ${originalValues.hasPackage ? "Present" : "None"} → ${updatePayload.hasPackage ? "Present" : "None"}`);
    }
    return items;
  };

  const createTenant = async (profile) => {
    setIsCreatingTenant(true);
    setTenantError("");
    setTenantFieldErrors({});
    try {
      const tenant = await onCreateTenant(profile);
      setTenantOptions((current) => current.some(({ id }) => id === tenant.id) ? current : [...current, tenant]);
      setForm((current) => ({ ...current, tenantId: String(tenant.id) }));
      setTenantQuery("");
      setCreatingTenant(false);
    } catch (error) {
      setTenantError(error.message);
      setTenantFieldErrors(error.data?.fields || {});
    } finally {
      setIsCreatingTenant(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!hasChanges) return;
    setPendingUpdate({ payload: updatePayload, items: changedItems() });
  };

  const removeTenant = () => {
    setForm((current) => ({ ...current, tenantId: "" }));
    setChoosingTenant(false);
    setTenantQuery("");
  };

  const restoreCurrentTenant = () => {
    setForm((current) => ({ ...current, tenantId: String(mailbox.tenant.id) }));
    setChoosingTenant(false);
    setTenantQuery("");
  };

  return (
    <>
      <div
        className="admin-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isSaving) onClose();
        }}
      >
        <section
          className="admin-modal admin-modal-wide"
          role="dialog"
          aria-modal={!creatingTenant && !pendingUpdate}
          aria-hidden={creatingTenant || Boolean(pendingUpdate) || undefined}
          aria-labelledby="mailbox-edit-title"
        >
          <div className="admin-modal-header">
            <div>
              <p className="admin-page-eyebrow">Mailbox assignment</p>
              <h2 id="mailbox-edit-title">Edit mailbox #{mailbox.number}</h2>
              <p>Assign a tenant, choose a size, and update current mail items.</p>
            </div>
            <button className="admin-modal-close" type="button" aria-label="Close mailbox editor" onClick={onClose} disabled={isSaving}>&times;</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="admin-modal-body">
              {submitError && <p className="admin-empty-state admin-error-state" role="alert">{submitError}</p>}
              <div className="admin-profile-sections">
                <fieldset className="admin-profile-section admin-mailbox-tenant-picker">
                  <legend>Tenant assignment</legend>
                  {mailbox.tenant && !choosingTenant ? (
                    <div className={`admin-mailbox-assignment-summary ${form.tenantId ? "" : "is-removing"}`.trim()}>
                      <div className="admin-assignment-details">
                        <span>{form.tenantId ? "Current tenant" : "Pending removal"}</span>
                        <strong>{tenantDisplayName(mailbox.tenant)}</strong>
                        {mailbox.tenant.email && <small>{mailbox.tenant.email}</small>}
                        {!form.tenantId && <p>This mailbox will become available after the changes are confirmed and saved.</p>}
                      </div>
                      <div className="admin-assignment-actions">
                        {form.tenantId ? (
                          <>
                            <button className="admin-button admin-button-secondary" type="button" onClick={() => setChoosingTenant(true)}>
                              Reassign
                            </button>
                            <button className="admin-button admin-button-danger" type="button" onClick={removeTenant}>
                              Remove
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="admin-button admin-button-secondary" type="button" onClick={restoreCurrentTenant}>
                              Undo Remove
                            </button>
                            <button className="admin-button admin-button-secondary" type="button" onClick={() => setChoosingTenant(true)}>
                              Reassign
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="admin-tenant-search">
                        Search existing tenants
                        <input
                          type="search"
                          placeholder="Search by tenant name, email, or business"
                          value={tenantQuery}
                          onChange={(event) => setTenantQuery(event.target.value)}
                        />
                      </label>
                      <label>
                        Tenant
                        <select
                          name="tenantId"
                          size="6"
                          value={form.tenantId}
                          onChange={(event) => setForm((current) => ({ ...current, tenantId: event.target.value }))}
                        >
                          {mailbox.tenant
                            ? <option value="" disabled>Select a tenant</option>
                            : <option value="">No tenant — make mailbox available</option>}
                          {visibleTenants.map((tenant) => (
                            <option key={tenant.id} value={tenant.id}>{tenantDisplayName(tenant)} ({tenant.email})</option>
                          ))}
                        </select>
                      </label>
                      <div className="admin-tenant-picker-actions">
                        <button className="admin-button admin-button-secondary" type="button" onClick={() => {
                          setTenantError("");
                          setTenantFieldErrors({});
                          setCreatingTenant(true);
                        }}>
                          Create New Tenant
                        </button>
                        {mailbox.tenant && (
                          <button className="admin-button admin-button-secondary" type="button" onClick={restoreCurrentTenant}>
                            Cancel Reassignment
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </fieldset>

                <fieldset className="admin-profile-section">
                  <legend>Mailbox settings</legend>
                  <div className="admin-form admin-profile-grid">
                    <label>
                      Mailbox size
                      <select value={form.size} onChange={(event) => setForm((current) => ({ ...current, size: event.target.value }))}>
                        {MAILBOX_SIZES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <div className="admin-mailbox-toggles">
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={form.hasMail}
                          onChange={(event) => setForm((current) => ({ ...current, hasMail: event.target.checked }))}
                        />
                        <span>Has mail letters</span>
                      </label>
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={form.hasPackage}
                          onChange={(event) => setForm((current) => ({ ...current, hasPackage: event.target.checked }))}
                        />
                        <span>Has UPS/FedEx package</span>
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
            <div className="admin-modal-footer">
              <div className="admin-form-actions">
                <button className="admin-button admin-button-secondary" type="button" onClick={onClose} disabled={isSaving}>Cancel</button>
                <button className="admin-button admin-button-primary" type="submit" disabled={isSaving || !hasChanges}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </section>
      </div>

      {creatingTenant && (
        <TenantProfileModal
          tenant={null}
          isSubmitting={isCreatingTenant}
          submitError={tenantError}
          externalErrors={tenantFieldErrors}
          onSubmit={createTenant}
          onClose={() => {
            if (!isCreatingTenant) setCreatingTenant(false);
          }}
        />
      )}

      {pendingUpdate && (
        <ConfirmModal
          title={`Update mailbox #${mailbox.number}?`}
          description="Please review and agree to these changes before saving."
          items={pendingUpdate.items}
          confirmLabel="Agree and Save"
          isSubmitting={isSaving}
          error={submitError}
          onConfirm={() => onSubmit(pendingUpdate.payload)}
          onCancel={() => setPendingUpdate(null)}
        />
      )}
    </>
  );
}
